/**
 * CreateUserModal — Settings → Create User.
 *
 * Super-admin-only surface that mints a new **Super Admin** under the caller's
 * active organization. Launched from the Header's Settings (gear) dropdown.
 *
 * The role is fixed: it is rendered as a read-only "Super Admin" field and is
 * never sent to the server (the API forces `super_admin` regardless). The new
 * account receives all permissions automatically via the role bypass, so there
 * is no permission picker here.
 *
 * Built on the shared `Modal` / `Input` / `Button` primitives so spacing,
 * typography, validation styling, and dark-mode all match the rest of the app.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { useToast } from '../ui/ToastProvider';
import { useCreateUser } from '../../../hooks/use-users';

// Same simplified RFC-5322 shape used by the Invite User modal — instant
// client feedback; the server re-validates with the identical intent.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FieldErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
}

export function CreateUserModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const createUser = useCreateUser();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  const reset = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setErrors({});
    setFormError(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  // Mirrors the server's CreateUserSchema rules so the user gets inline
  // feedback before the round-trip. Returns the next error map (also used to
  // decide whether to submit).
  const validate = (): FieldErrors => {
    const next: FieldErrors = {};
    if (!firstName.trim()) next.firstName = t('components.createUserModal.errFirstNameRequired');
    if (!lastName.trim()) next.lastName = t('components.createUserModal.errLastNameRequired');
    if (!email.trim()) {
      next.email = t('components.createUserModal.errEmailRequired');
    } else if (!EMAIL_RE.test(email.trim())) {
      next.email = t('components.createUserModal.errEmailInvalid');
    }
    if (!password) {
      next.password = t('components.createUserModal.errPasswordRequired');
    } else if (
      password.length < 8 ||
      !/[A-Z]/.test(password) ||
      !/[0-9]/.test(password)
    ) {
      next.password = t('components.createUserModal.errPasswordWeak');
    }
    if (confirmPassword !== password) {
      next.confirmPassword = t('components.createUserModal.errPasswordsMismatch');
    }
    return next;
  };

  const submit = async () => {
    setFormError(null);
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    try {
      const user = await createUser.mutateAsync({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        password,
      });
      showToast(
        t('components.createUserModal.toastCreated', {
          name: `${user.firstName} ${user.lastName}`,
        }),
        'success',
      );
      reset();
      onCreated?.();
      onClose();
    } catch (err: unknown) {
      // Surface the real server reason (e.g. "A user with this email already
      // exists" / first field-level validation message) rather than a generic
      // fallback. Mirrors the Invite User modal's defensive error extraction.
      const e = err as {
        response?: { data?: { error?: { message?: string; details?: Record<string, string[]> }; message?: string } };
        message?: string;
      };
      const data = e?.response?.data;
      const rawMessage =
        data?.error?.message ??
        data?.message ??
        e?.message ??
        t('components.createUserModal.errCreateFailed');
      const looksLikeJson = /^\s*[[{]/.test(rawMessage);
      setFormError(looksLikeJson ? t('components.createUserModal.errCreateFailed') : rawMessage);
    }
  };

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <Button variant="ghost" onClick={close} disabled={createUser.isPending}>
        {t('common.cancel')}
      </Button>
      <Button
        onClick={submit}
        loading={createUser.isPending}
        disabled={createUser.isPending}
        data-testid="create-user-submit"
      >
        {createUser.isPending
          ? t('components.createUserModal.creating')
          : t('components.createUserModal.title')}
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={close}
      title={t('components.createUserModal.title')}
      size="lg"
      footer={footer}
      // Data-entry form — an accidental backdrop click shouldn't discard
      // the typed name / email / password.
      closeOnBackdrop={false}
    >
      <div className="space-y-4" data-testid="create-user-modal">
        {/* Role banner — fixed to Super Admin, not editable. */}
        <div
          className="flex items-start gap-3 rounded-md px-3 py-2.5"
          style={{
            backgroundColor: 'var(--vz-light, #f3f4f6)',
            border: '1px solid var(--vz-border-color, rgba(0,0,0,0.08))',
          }}
        >
          <ShieldCheck
            className="shrink-0"
            style={{ width: 18, height: 18, marginTop: 1, color: 'var(--vz-primary, #468AD4)' }}
          />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--vz-heading-color)' }}>
              {t('components.createUserModal.roleSuperAdmin')}
            </div>
            <div style={{ fontSize: 12, color: 'var(--vz-body-secondary-color, #6b7280)' }}>
              {t('components.createUserModal.roleDescription')}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label={t('components.createUserModal.firstName')}
            requiredMarker
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            error={errors.firstName}
            placeholder={t('components.createUserModal.firstNamePlaceholder')}
            autoFocus
            data-testid="create-user-first-name"
          />
          <Input
            label={t('components.createUserModal.lastName')}
            requiredMarker
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            error={errors.lastName}
            placeholder={t('components.createUserModal.lastNamePlaceholder')}
            data-testid="create-user-last-name"
          />
        </div>

        <Input
          label={t('components.createUserModal.email')}
          type="email"
          requiredMarker
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
          placeholder={t('components.createUserModal.emailPlaceholder')}
          data-testid="create-user-email"
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label={t('components.createUserModal.password')}
            type="password"
            requiredMarker
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={errors.password}
            placeholder="••••••••"
            autoComplete="new-password"
            data-testid="create-user-password"
          />
          <Input
            label={t('components.createUserModal.confirmPassword')}
            type="password"
            requiredMarker
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={errors.confirmPassword}
            placeholder="••••••••"
            autoComplete="new-password"
            data-testid="create-user-confirm-password"
          />
        </div>
        <p style={{ fontSize: 11, color: 'var(--vz-body-secondary-color, #6b7280)' }}>
          {t('components.createUserModal.passwordHint')}
        </p>

        {formError && (
          <div
            className="rounded text-sm px-3 py-2"
            style={{
              backgroundColor: 'rgba(237,94,94,0.08)',
              border: '1px solid rgba(237,94,94,0.3)',
              color: '#b42424',
            }}
            data-testid="create-user-error"
          >
            {formError}
          </div>
        )}
      </div>
    </Modal>
  );
}
